/**
 * JPEG のマーカーセグメントを操作する純粋関数群。
 *
 * 画素データ (SOS 以降のエントロピー符号) には一切触れず、
 * SOI から SOS 直前までのヘッダ領域のみを対象とする。
 */

/** Exif を格納する APP1 */
const MARKER_APP1 = 0xe1;
/** JFIF を格納する APP0。出力側のエンコーダが自前で書くため引き継がない */
const MARKER_APP0 = 0xe0;
/** スキャン開始。ここから先はエントロピー符号なので走査を打ち切る */
const MARKER_SOS = 0xda;
/** コメント */
const MARKER_COM = 0xfe;

/** 長さフィールドを持たない単独マーカー (RSTn / SOI / EOI / TEM) */
const isStandaloneMarker = (marker: number): boolean => (
  marker === 0xd8 || marker === 0xd9 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)
);

/** フレームヘッダ (SOF0〜SOF15 のうち DHT/JPG/DAC を除いたもの) */
const isFrameMarker = (marker: number): boolean => (
  marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
);

/** 引き継ぎ対象のセグメント。APP1〜APP15 (Exif/ICC/XMP 等) とコメント */
const isCarrySegment = (marker: number): boolean => (
  (marker > MARKER_APP0 && marker <= 0xef) || marker === MARKER_COM
);

export type JpegSegment = {
  marker: number,
  /** 先頭の 0xFF の位置 */
  start: number,
  /** セグメント終端 (次のマーカーの位置) */
  end: number,
};

export type JpegFrameInfo = {
  width: number,
  height: number,
  /** 最大サンプリング係数から求めた MCU の幅 (4:2:0 なら 16) */
  mcuWidth: number,
  /** 同じく MCU の高さ */
  mcuHeight: number,
};

/**
 * SOI 直後から SOS 直前までのマーカーセグメントを列挙する。
 * 構造が壊れている場合はそこまでの結果を返す。
 */
export const parseSegments = (bytes: Uint8Array): JpegSegment[] => {
  const segments: JpegSegment[] = [];
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return segments;
  }
  let i = 2;
  while (i + 1 < bytes.length) {
    /* パディングの 0xFF を読み飛ばす */
    if (bytes[i] !== 0xff) {
      break;
    }
    let markerIndex = i;
    while (markerIndex + 1 < bytes.length && bytes[markerIndex + 1] === 0xff) {
      markerIndex += 1;
    }
    const marker = bytes[markerIndex + 1];
    if (marker === MARKER_SOS) {
      break;
    }
    if (isStandaloneMarker(marker)) {
      i = markerIndex + 2;
      continue;
    }
    if (markerIndex + 4 > bytes.length) {
      break;
    }
    const length = (bytes[markerIndex + 2] << 8) | bytes[markerIndex + 3];
    /* 長さは自身の 2 バイトを含むため 2 未満は不正 */
    if (length < 2 || markerIndex + 2 + length > bytes.length) {
      break;
    }
    segments.push({ marker, start: markerIndex, end: markerIndex + 2 + length });
    i = markerIndex + 2 + length;
  }
  return segments;
};

/**
 * SOF から画像の実寸と MCU サイズを読み取る。
 * MCU サイズは jpegtran のロスレス変換可否の判定に使う。
 */
export const readFrameInfo = (bytes: Uint8Array): JpegFrameInfo | null => {
  const segments = parseSegments(bytes);
  const frame = segments.find((v) => isFrameMarker(v.marker));
  if (!frame) {
    return null;
  }
  const p = frame.start;
  const height = (bytes[p + 5] << 8) | bytes[p + 6];
  const width = (bytes[p + 7] << 8) | bytes[p + 8];
  const componentCount = bytes[p + 9];
  if (!width || !height || !componentCount) {
    return null;
  }
  let maxH = 1;
  let maxV = 1;
  for (let k = 0; k < componentCount; k += 1) {
    const o = p + 10 + k * 3;
    if (o + 1 >= bytes.length) {
      return null;
    }
    const h = bytes[o + 1] >> 4;
    const v = bytes[o + 1] & 0x0f;
    if (h > maxH) maxH = h;
    if (v > maxV) maxV = v;
  }
  /* libjpeg の iMCU サイズ算出と同じ (DCTSIZE = 8) */
  return { width, height, mcuWidth: maxH * 8, mcuHeight: maxV * 8 };
};

/** 対象セグメントが Exif を格納した APP1 かどうか */
const isExifApp1 = (bytes: Uint8Array, segment: JpegSegment): boolean => {
  if (segment.marker !== MARKER_APP1 || segment.end - segment.start < 10) {
    return false;
  }
  /* 識別子 'Exif\0\0' */
  const id = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];
  for (let i = 0; i < id.length; i += 1) {
    if (bytes[segment.start + 4 + i] !== id[i]) {
      return false;
    }
  }
  return true;
};

/**
 * 元ファイルから引き継ぐセグメント (APP1〜APP15 とコメント) を抽出する。
 * ICC プロファイルのように複数の APP2 に分割されているものも順序を保って返す。
 */
export const extractCarrySegments = (bytes: Uint8Array): Uint8Array[] => (
  parseSegments(bytes)
    .filter((v) => isCarrySegment(v.marker))
    .map((v) => bytes.slice(v.start, v.end))
);

/**
 * 引き継ぎ対象のセグメントを取り除いたバイナリを返す。
 * 挿入前に呼ぶことで、変換後に残っている古い Exif/ICC との重複を防ぐ。
 */
export const stripCarrySegments = (bytes: Uint8Array): Uint8Array => {
  const targets = parseSegments(bytes).filter((v) => isCarrySegment(v.marker));
  if (!targets.length) {
    return bytes;
  }
  const removed = targets.reduce((sum, v) => sum + (v.end - v.start), 0);
  const result = new Uint8Array(bytes.length - removed);
  let write = 0;
  let read = 0;
  targets.forEach((target) => {
    result.set(bytes.subarray(read, target.start), write);
    write += target.start - read;
    read = target.end;
  });
  result.set(bytes.subarray(read), write);
  return result;
};

/**
 * セグメント群を挿入する。JFIF(APP0) がある場合はその直後、無ければ SOI 直後。
 */
export const insertSegments = (bytes: Uint8Array, segments: Uint8Array[]): Uint8Array => {
  if (!segments.length) {
    return bytes;
  }
  const parsed = parseSegments(bytes);
  const app0 = parsed.find((v) => v.marker === MARKER_APP0);
  const insertAt = app0 ? app0.end : 2;
  const total = segments.reduce((sum, v) => sum + v.length, 0);
  const result = new Uint8Array(bytes.length + total);
  result.set(bytes.subarray(0, insertAt), 0);
  let write = insertAt;
  segments.forEach((segment) => {
    result.set(segment, write);
    write += segment.length;
  });
  result.set(bytes.subarray(insertAt), write);
  return result;
};

/**
 * Exif の APP1 内にある Orientation タグの位置を探す。
 * @returns 値フィールドの絶対オフセットとエンディアン。見つからなければ null
 */
const findOrientationEntry = (segment: Uint8Array): { offset: number, little: boolean } | null => {
  const self: JpegSegment = { marker: segment[1], start: 0, end: segment.length };
  if (!isExifApp1(segment, self)) {
    return null;
  }
  /* 0xFF 0xE1 + 長さ 2 バイト + 識別子 6 バイト = TIFF ヘッダ先頭 */
  const tiff = 10;
  if (tiff + 8 > segment.length) {
    return null;
  }
  const little = segment[tiff] === 0x49 && segment[tiff + 1] === 0x49;
  const big = segment[tiff] === 0x4d && segment[tiff + 1] === 0x4d;
  if (!little && !big) {
    return null;
  }
  const view = new DataView(segment.buffer, segment.byteOffset, segment.byteLength);
  const ifd0 = tiff + view.getUint32(tiff + 4, little);
  if (ifd0 + 2 > segment.length) {
    return null;
  }
  const count = view.getUint16(ifd0, little);
  for (let k = 0; k < count; k += 1) {
    const entry = ifd0 + 2 + k * 12;
    if (entry + 12 > segment.length) {
      break;
    }
    if (view.getUint16(entry, little) === 0x0112) {
      /* SHORT(型3) は 4 バイトの値フィールドの先頭 2 バイトに格納される */
      return { offset: entry + 8, little };
    }
  }
  return null;
};

/**
 * JPEG バイナリから Exif Orientation を読む。
 * Exif が無い / タグが無い / 解釈できない場合は 1 (変換不要) を返す。
 */
export const readExifOrientation = (bytes: Uint8Array): number => {
  const segments = parseSegments(bytes);
  for (const segment of segments) {
    if (!isExifApp1(bytes, segment)) {
      continue;
    }
    const app1 = bytes.slice(segment.start, segment.end);
    const found = findOrientationEntry(app1);
    if (!found) {
      continue;
    }
    const view = new DataView(app1.buffer, app1.byteOffset, app1.byteLength);
    const value = view.getUint16(found.offset, found.little);
    return value >= 1 && value <= 8 ? value : 1;
  }
  return 1;
};

/**
 * Exif の APP1 セグメント内にある Orientation タグの値だけを書き換える。
 * 対象が Exif の APP1 でない場合や Orientation タグが無い場合は入力をそのまま返す。
 *
 * 画像を実回転させた後は必ず 1 (TOP_LEFT) に落とすこと。
 * さもないと表示側が二重に回転させてしまう。
 */
export const setExifOrientation = (segment: Uint8Array, value: number): Uint8Array => {
  const found = findOrientationEntry(segment);
  if (!found) {
    return segment;
  }
  const result = segment.slice();
  const view = new DataView(result.buffer, result.byteOffset, result.byteLength);
  view.setUint16(found.offset, value, found.little);
  return result;
};
